/* eslint-disable no-bitwise */
import { BehaviorSubject } from 'rxjs';
import { timeout, wait } from 'ngx-core-services';

import {
  CommandPriority,
  ScheduleModbusCommand,
  fromNBytesToNumber,
  fromNumberToNBytes,
  getCountryNameFromCodeV3,
} from 'ngx-modbus';

import {
  SgpCommandDistributeFirmware,
  SgpCommandGetFirmwareVersion,
  SgpCommandHandshake,
  SgpCommandStartFirmwareDownload,
  SgpCommandValidateFirmware,
} from './command.model';
import {
  CancellationSignal,
  ReturnDistributeCmd,
  SafetyMetadata,
  SgpCommandCode,
  SgpDeviceType,
  SgpFlagMode,
  SgpMetadataFirmwareType,
} from './general.model';
import { SgpCommandBuilder } from './sgp-command-builder.model';
import { SgpResponseDecoder } from './sgp-response-decoder.models';
import { SgpSupport } from './sgp-support.model';
import { ResponseDetails } from './response.model';
import { parseData, parseSafetyCustomHeader, buildSafetyOverrideKey, splitUint8Array } from './sgp-interaction.utils';

export class SgpInteraction {
  private readonly execute: (cmd: Uint8Array) => Promise<Uint8Array>;
  private readonly slaveId: number;
  private readonly schedule: ScheduleModbusCommand;
  private readonly MAX_TIMEOUT_IN_MS = 30000;
  readonly upgradeProgress$ = new BehaviorSubject<ResponseDetails | null>(null);

  constructor(
    execute: (cmd: Uint8Array) => Promise<Uint8Array>,
    slaveId: number,
    deviceId?: string,
  ) {
    this.execute = execute;
    this.slaveId = slaveId;

    if (deviceId) {
      this.schedule = ScheduleModbusCommand.getInstanceForDevice(deviceId);
      console.log(`[SgpInteraction] Using scheduler for deviceId="${deviceId}" (shared with ModbusLink on same bus)`);
    } else {
      this.schedule = ScheduleModbusCommand.getInstance();
      console.log('[SgpInteraction] Using default scheduler (no deviceId provided)');
    }
  }

  interaction(cmd: Uint8Array, MAX_TIMEOUT_IN_MS = this.MAX_TIMEOUT_IN_MS) {
    const createdAt = new Date();
    const executeWithDelay = async () => this.execute(cmd);

    return this.schedule.execute(
      () => Promise.race([
        executeWithDelay(),
          timeout(MAX_TIMEOUT_IN_MS).then(() => { throw new Error('Timeout'); }),
        ]),
      CommandPriority.High,
      createdAt,
      createdAt,
    );
  }

  /**
   * Handshake
   */
  async handshake() {
    const handshakeParams: SgpCommandHandshake = {
      type: SgpCommandCode.Handshake,
      protocolVersion: SgpCommandBuilder.ProtocolVersion,
      maxSizeOfSingleBuffer: SgpCommandBuilder.SizeOfReceivingBuffer,
      masterDeviceType: SgpCommandBuilder.DeviceType,
    };
    const handshakeCmd = SgpCommandBuilder.BuildCommand(SgpFlagMode.Normal, handshakeParams);

    return SgpResponseDecoder.DecodeResponse(await this.interaction(handshakeCmd));
  }

  async getFirmwareVersion() {
    /**
     * Get Firmware Version (ARM)
     */
    const getFirmwareVersionParams: SgpCommandGetFirmwareVersion = {
      type: SgpCommandCode.GetFirmwareVersion,
      slaveDeviceType: SgpDeviceType.Core,
    };
    const getFirmwareVersionCmd = SgpCommandBuilder.BuildCommand(SgpFlagMode.Normal, getFirmwareVersionParams);

    return SgpResponseDecoder.DecodeResponse(await this.interaction(getFirmwareVersionCmd));
  }

  async startFirmwareUpdate(firmwareData: SgpMetadataFirmwareType) {
    const startFirmwareDownloadParams: SgpCommandStartFirmwareDownload = {
      type: SgpCommandCode.StartFirmwareDownload,
      firmwareName: firmwareData.firmwareName,
      firmwareVersion : firmwareData.firmwareVersion,
      firmwareSize : firmwareData.firmwareSize,
      firmwareCRC32: firmwareData.firmwareCRC32,
      // if firmware version is not present, then it is a safety firmware
      firmwareEffect: firmwareData.firmwareVersion ? firmwareData.firmwareEffect : new Uint8Array([0x04]),
    };

    const startFirmwareDownloadCmd = SgpCommandBuilder.BuildCommand(SgpFlagMode.Normal, startFirmwareDownloadParams);

    return SgpResponseDecoder.DecodeResponse(await this.interaction(startFirmwareDownloadCmd));
  }

  async distributeFirmware(
    firmwareData: SgpMetadataFirmwareType,
    data: ArrayBuffer,
    progress: (p: number) => void,
    cancellationSignal?: CancellationSignal,
  ) {
    let maxSizeOfDistributionFw = SgpCommandBuilder.SizeOfReceivingBuffer;
    const maxSizeOfDistrFwInBytes = fromNumberToNBytes(maxSizeOfDistributionFw, 4);

    /**
     * Distribute Firmware (ARM)
     */
    const fwSize = data.byteLength;
    const numberOfPackets = Math.ceil(fwSize / maxSizeOfDistributionFw);
    const fwOffset = fromNBytesToNumber(firmwareData.startSoftwareAddress);


    for (let i = 0; i < numberOfPackets; i++) {
      // if last
      if (i === numberOfPackets - 1) {
        maxSizeOfDistributionFw = fwSize - i * maxSizeOfDistributionFw;
      }
      // log progress
      console.log(`Distributing firmware: ${i + 1}/${numberOfPackets}`);
      progress(Math.floor((i + 1) / numberOfPackets * 100));
      const offset = i * maxSizeOfDistributionFw;
      const offsetAddress = fromNumberToNBytes(fwOffset + offset, 4);

      const distributeFirmwareParams: SgpCommandDistributeFirmware = {
        type: SgpCommandCode.DistributeFirmware,
        fileCRC32: firmwareData.firmwareCRC32,
        offsetAddress,
        dataLength: maxSizeOfDistrFwInBytes,
        data: SgpSupport.QueryFrame(data, offset, maxSizeOfDistributionFw),
      };
      let retries = 0;
      let success = false;
      const MAX_TIMEOUT = 2 * 1000;
      const MAX_RETRIES = 5;
      while (retries < MAX_RETRIES && !success) {
        const distributeFirmwareCmd = SgpCommandBuilder.BuildCommand(SgpFlagMode.Normal, distributeFirmwareParams);

        let distributeFirmwareResponse;
        try {
          distributeFirmwareResponse = SgpResponseDecoder.DecodeResponse(await this.interaction(distributeFirmwareCmd, MAX_TIMEOUT));
        } catch {
          await wait(MAX_TIMEOUT);
          console.log(`Distribute Firmware failed, retrying... (${retries + 1}/${MAX_RETRIES})`);
          retries++;
          continue;
        }

        if (distributeFirmwareResponse.infoField?.value.commandResponseType === SgpCommandCode.DistributeFirmware &&
            distributeFirmwareResponse.infoField.value.returnCode === ReturnDistributeCmd.Normal) {
          success = true;
        } else {
          console.log(`Distribute Firmware failed, retrying... (${retries + 1}/${MAX_RETRIES})`);
          retries++;
          continue;
        }
      }

      if (cancellationSignal?.cancelled) {
        console.error(`Distribute Firmware cancelled`);
        return false;
      }

      if (!success) {
        console.log(`Distribute Firmware failed after ${MAX_RETRIES} retries`);
        return false;
      }
    }
    return true;
  }

  async validateFirmware(firmwareData: SgpMetadataFirmwareType, isLast: boolean) {
    /**
     * Validate Firmware (ARM)
     */
    const upgradeTime = new Uint8Array(6);
    upgradeTime.set([0x00, 0x00, 0x00, 0x00, 0x00, isLast ? 0x00 : 0x01]);
    const validateFirmwareParams: SgpCommandValidateFirmware = {
      type: SgpCommandCode.ValidateFirmware,
      fileCRC32: firmwareData.firmwareCRC32,
      upgradeTime,
    };

    const validateFirmwareCmd = SgpCommandBuilder.BuildCommand(SgpFlagMode.Normal, validateFirmwareParams);

    return SgpResponseDecoder.DecodeResponse(await this.interaction(validateFirmwareCmd));
  }

  async upgradeInNormalMode(
    firmwareData: SgpMetadataFirmwareType,
    data: ArrayBuffer,
    isLast: boolean,
    progress: (
      p: number, // progress in %
      f: number, // phase
      e: string, // error message
      z: string[], // phase names
    ) => void,
    cancellationSignal?: CancellationSignal,
  ) {
    const totalPhases = 5;
    const otherPhasesWeight = 10 / (totalPhases - 1); // 10% for all phases other than "Distribute Firmware"
    const phaseNames = [
      'Handshake',
      'Get Firmware Version',
      'Start Firmware Update',
      'Distribute Firmware',
      'Validate Firmware',
    ];

    try {
      // Phase 1: Handshake
      progress(0, 0, '', phaseNames);
      if (cancellationSignal?.cancelled) {
        throw new Error('Handshake cancelled');
      }
      const handshakeResponse = await this.handshake();
      if (handshakeResponse.infoField?.value.commandResponseType !== SgpCommandCode.Handshake) {
        throw new Error('Handshake failed');
      }

      // Phase 2: Get Firmware Version
      progress(otherPhasesWeight, 1, '', phaseNames);
      if (cancellationSignal?.cancelled) {
        throw new Error('Get Firmware Version cancelled');
      }
      const getFirmwareVersionResponse = await this.getFirmwareVersion();
      if (getFirmwareVersionResponse.infoField?.value.commandResponseType !== SgpCommandCode.GetFirmwareVersion) {
        throw new Error('Get Firmware Version failed');
      }

      // Phase 3: Start Firmware Update
      progress(otherPhasesWeight * 2, 2, '', phaseNames);
      if (cancellationSignal?.cancelled) {
        throw new Error('Start Firmware Update cancelled');
      }
      const startFirmwareDownloadResponse = await this.startFirmwareUpdate(firmwareData);
      if (startFirmwareDownloadResponse.infoField?.value.commandResponseType !== SgpCommandCode.StartFirmwareDownload) {
        throw new Error('Start Firmware Download failed');
      }

      // Phase 4: Distribute Firmware
      const distributeFirmwareResponse = await this.distributeFirmware(firmwareData, data, (p) => {
        progress(otherPhasesWeight * 2 + p * 0.9, 3, '', phaseNames); // Distribute Firmware takes 90% of the progress
      }, cancellationSignal);
      if (distributeFirmwareResponse === false) {
        throw new Error('Distribute Firmware failed');
      }

      // Phase 5: Validate Firmware
      progress(100 - otherPhasesWeight, 4, '', phaseNames);
      if (cancellationSignal?.cancelled) {
        throw new Error('Validate Firmware cancelled');
      }
      const validateFirmwareResponse = await this.validateFirmware(firmwareData, isLast);
      if (
        validateFirmwareResponse.infoField?.value.commandResponseType !== SgpCommandCode.ValidateFirmware ||
        validateFirmwareResponse.validationField?.isValid !== true
      ) {
        throw new Error('Validate Firmware failed');
      }

      progress(100, totalPhases, '', phaseNames);
    } catch (error) {
      if (error instanceof Error) {
        progress(0, 0, error.message, phaseNames);
      } else {
        progress(0, 0, 'Unknown error occurred', phaseNames);
      }
    }
  }

  static ExtractMetadataFromSafetyData(data: ArrayBuffer | null, customContents: Uint8Array[]): SafetyMetadata[] {
    const safetyMetadataMap = new Map<string, SafetyMetadata>();

    if (data) {
      const safetyData = new Uint8Array(data);
      const isSafety = String.fromCharCode(...safetyData.slice(0, 4)) === 'SAFE';
      if (!isSafety) {
        throw new Error('Invalid safety data');
      }

      const firmwareSize = fromNBytesToNumber(safetyData.slice(4, 8));
      const separator = safetyData.slice(12, 16);

      const safetyCount = safetyData[20];

      const safetyContentAndMetadata = safetyData.slice(22, firmwareSize);

      const metadata = splitUint8Array(safetyContentAndMetadata, separator);
      metadata.pop();

      if (metadata.length !== safetyCount) {
        throw new Error('Invalid safety data');
      }

      for (const details of metadata) {
        const countryCode = details[0];
        const regionCode = details[2];
        const regionIndex = details[3];

        const dataPointer = fromNBytesToNumber(details.slice(4, 8));
        const dataLength = fromNBytesToNumber(details.slice(8, 10));

        const countryName = String.fromCharCode(...details.slice(10, 10 + 16));
        const regionName = String.fromCharCode(...details.slice(26, 26 + 16));

        const content = String.fromCharCode(...safetyData.slice(dataPointer, dataPointer + dataLength));
        const parsedContent = parseData(content);

        // Extract version from header (countryCode/regionCode come from binary metadata)
        const headerData = parseSafetyCustomHeader(content);
        const version = headerData?.version;

        const key = `${countryCode}_${regionCode}_${version}`;
        safetyMetadataMap.set(key, {
          content,
          countryCode,
          countryName,
          dataLength,
          parsedContent,
          dataPointer,
          regionCode,
          regionIndex,
          regionName,
        });
      }
    }

    for (const details of customContents) {
      const content = String.fromCharCode(...details);
      const headerData = parseSafetyCustomHeader(content);

      if (!headerData) {
        throw new Error('Invalid custom safety data');
      }

      const { countryCode, regionCode } = headerData;

      /**
       * format: 0x0000, bit 8-15: country code (0-255), bit 0-7: region
       */
      const countryRegionCode = (countryCode << 8) + regionCode;
      const parsedContent = parseData(content);

      const countryName = getCountryNameFromCodeV3(countryRegionCode);
      const regionName = '';

      safetyMetadataMap.set(buildSafetyOverrideKey(headerData), {
        content,
        countryCode,
        countryName,
        dataLength: 0,
        parsedContent,
        dataPointer: 0,
        regionCode,
        regionIndex: 0,
        regionName,
      });
    }

    return Array.from(safetyMetadataMap.values());
  }

  static ExtractMetadataFromFirmware(data: Uint8Array): SgpMetadataFirmwareType {
    const rData = [...data].reverse();
    const version = rData[1023];

    switch (version) {
      case 1:
        return this.ExtractMetadataFromFirmwareV1(data);
      case 2:
        return this.ExtractMetadataFromFirmwareV2(data);
      case 3:
        return this.ExtractMetadataFromFirmwareV3(data);

      default:
        throw new Error('Unknown firmware structure version');
    }
  }

  static ExtractMetadataFromFirmwareV1(data: Uint8Array): SgpMetadataFirmwareType {
    const ONE_KB = 1024;

    const metadata = [...data].slice(-ONE_KB);
    const fwChecksum = new Uint8Array(metadata.slice(1, 5));
    const fwValidation = new Uint8Array(metadata.slice(5, 9));
    const chipModel = String.fromCharCode(...metadata.slice(9, 39)).replace(/\0/g, '');
    const softwareVersion = String.fromCharCode(...metadata.slice(39, 59)).replace(/\0/g, '');
    const hardwareVersion = String.fromCharCode(...metadata.slice(59, 79)).replace(/\0/g, '');
    const firmwareName = String.fromCharCode(...metadata.slice(79, 109)).replace(/\0/g, '');
    const buildDate = String.fromCharCode(...metadata.slice(109, 121)).replace(/\0/g, '');
    const startSoftwareAddress = new Uint8Array(metadata.slice(121, 125));
    const upgradeType = new Uint8Array(metadata.slice(125, 126));
    const chipCode = new Uint8Array(metadata.slice(126, 128));

    /**
     * @important It is not in the documentation
     * the last 12 bytes are reserved for additional information, in particular
     * between the 4 and 8 byte there is the size of the firmware.
     */
    const fwSize = new Uint8Array([...data].slice(-ONE_KB - 12 + 4, -ONE_KB - 4));

    return {
      firmwareCRC32: fwValidation,
      firmwareEffect: upgradeType,
      firmwareName,
      firmwareSize : fwSize,
      firmwareVersion : softwareVersion,
      startSoftwareAddress,

      // optional
      buildDate,
      chipCode,
      chipModel,
      fwChecksum,
      hardwareVersion,
    };
  }

  static ExtractMetadataFromFirmwareV2(data: Uint8Array): SgpMetadataFirmwareType {
    const ONE_KB = 1024;

    const metadata = [...data].slice(-ONE_KB);
    const firmwareSize = new Uint8Array(metadata.slice(1, 5));
    const firmwareCRC32 = new Uint8Array(metadata.slice(5, 9));
    const chipModel = String.fromCharCode(...metadata.slice(9, 39)).replace(/\0/g, '');
    const firmwareVersion = String.fromCharCode(...metadata.slice(39, 59)).replace(/\0/g, '');
    const hardwareVersion = String.fromCharCode(...metadata.slice(59, 79)).replace(/\0/g, '');
    const firmwareName = String.fromCharCode(...metadata.slice(79, 109)).replace(/\0/g, '');
    const buildDate = String.fromCharCode(...metadata.slice(109, 121)).replace(/\0/g, '');
    const startSoftwareAddress = new Uint8Array(metadata.slice(121, 125));
    const firmwareEffect = new Uint8Array(metadata.slice(125, 126)); // Using chip role as firmware effect
    const chipCode = new Uint8Array(metadata.slice(126, 128)); // Using 2 bytes as specified

    return {
        firmwareName,
        firmwareVersion,
        firmwareSize,
        firmwareCRC32,
        firmwareEffect,
        startSoftwareAddress,

        // optional
        chipModel,
        hardwareVersion,
        buildDate,
        chipCode,
    };
  }

  static ExtractMetadataFromFirmwareV3(data: Uint8Array): SgpMetadataFirmwareType {
    const ONE_KB = 1024;

    const metadata = [...data].slice(-ONE_KB);
    const firmwareSize = new Uint8Array(metadata.slice(1, 5));
    const firmwareCRC32 = new Uint8Array(metadata.slice(5, 9));
    const chipModel = String.fromCharCode(...metadata.slice(9, 39)).replace(/\0/g, '');
    const firmwareVersion = String.fromCharCode(...metadata.slice(39, 59)).replace(/\0/g, '');
    const hardwareVersion = String.fromCharCode(...metadata.slice(59, 79)).replace(/\0/g, '');
    const firmwareName = String.fromCharCode(...metadata.slice(79, 109)).replace(/\0/g, '');
    const buildDate = String.fromCharCode(...metadata.slice(109, 121)).replace(/\0/g, '');
    const startSoftwareAddress = new Uint8Array(metadata.slice(121, 125));
    const firmwareEffect = new Uint8Array(metadata.slice(125, 126)); // Using chip role as firmware effect
    const chipCode = new Uint8Array(metadata.slice(126, 128)); // Using 2 bytes as specified

    return {
        firmwareName,
        firmwareVersion,
        firmwareSize,
        firmwareCRC32,
        firmwareEffect,
        startSoftwareAddress,

        // optional
        chipModel,
        hardwareVersion,
        buildDate,
        chipCode,
    };
  }
}
